import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { customersAPI, deviceLinksAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Activity, Zap, Sun, Battery, Wifi, WifiOff, AlertTriangle,
  RefreshCw, LayoutGrid, List, BarChart3, ChevronRight,
  Loader2, Thermometer, ArrowDownToLine, ArrowUpFromLine,
  Server, Plug, Filter, X, Search, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

const STATUS_CONFIG = {
  0: { label: 'Offline', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: WifiOff },
  1: { label: 'Online', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Wifi },
  2: { label: 'Fault', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertTriangle },
  3: { label: 'Offline', color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400', icon: WifiOff },
};

const PLATFORM_COLORS = {
  foxess: 'bg-orange-500',
  solis: 'bg-blue-500',
  growatt: 'bg-green-500',
  tesla: 'bg-red-500',
};

export const Monitoring = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceDetail, setDeviceDetail] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [generationData, setGenerationData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('monitoring_view') || 'cards');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [linkDialog, setLinkDialog] = useState({ open: false, device: null });

  // Filters
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterBattery, setFilterBattery] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchOverview = useCallback(async () => {
    try {
      const res = await api.get('/monitoring/overview');
      setOverview(res.data);
    } catch (err) {
      toast.error('Failed to load monitoring data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/monitoring/foxess/cache/clear');
      await fetchOverview();
      toast.success('Data refreshed');
    } catch {
      toast.error('Refresh failed');
      setRefreshing(false);
    }
  };

  const handleViewDevice = async (device) => {
    setSelectedDevice(device);
    setDetailOpen(true);
    setDetailLoading(true);
    setDeviceDetail(null);
    setRealtimeData(null);
    setGenerationData(null);
    // Only fetch detail for Fox ESS devices (Growatt uses different endpoints)
    if (device.platform === 'foxess') {
      try {
        const [rtRes, genRes, detRes] = await Promise.all([
          api.get(`/monitoring/foxess/device/${device.sn}/realtime`),
          api.get(`/monitoring/foxess/device/${device.sn}/generation`),
          api.get(`/monitoring/foxess/device/${device.sn}/detail`),
        ]);
        setRealtimeData(rtRes.data);
        setGenerationData(genRes.data);
        setDeviceDetail(detRes.data);
      } catch {
        toast.error('Failed to load device details');
      }
    }
    setDetailLoading(false);
  };

  const changeView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('monitoring_view', mode);
  };

  // Apply filters
  const devices = overview?.devices || [];
  const filteredDevices = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return devices.filter(d => {
      if (q && !(
        (d.name || '').toLowerCase().includes(q) ||
        (d.sn || '').toLowerCase().includes(q) ||
        (d.station_name || '').toLowerCase().includes(q)
      )) return false;
      if (filterManufacturer !== 'all' && d.manufacturer !== filterManufacturer) return false;
      if (filterType !== 'all' && d.product_family !== filterType) return false;
      if (filterBattery === 'yes' && !d.has_battery) return false;
      if (filterBattery === 'no' && d.has_battery) return false;
      if (filterStatus === 'online' && d.status !== 1) return false;
      if (filterStatus === 'offline' && d.status !== 0 && d.status !== 3) return false;
      if (filterStatus === 'fault' && d.status !== 2) return false;
      return true;
    });
  }, [devices, filterManufacturer, filterType, filterBattery, filterStatus, searchQuery]);

  const activeFilterCount = [filterManufacturer, filterType, filterBattery, filterStatus].filter(f => f !== 'all').length + (searchQuery.trim() ? 1 : 0);

  const clearFilters = () => {
    setFilterManufacturer('all');
    setFilterType('all');
    setFilterBattery('all');
    setFilterStatus('all');
    setSearchQuery('');
  };

  const filterOptions = overview?.filter_options || {};
  const manufacturers = filterOptions.manufacturers || [];
  const deviceTypes = filterOptions.device_types || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64" data-testid="monitoring-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  const platforms = overview?.platforms || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="monitoring-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-['Manrope']">Monitoring</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Real-time solar inverter monitoring across platforms
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative" data-testid="search-wrapper">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search name, SN, address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-48 pl-9 pr-3 text-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1" data-testid="view-toggle">
              {[
                { mode: 'cards', icon: LayoutGrid },
                { mode: 'table', icon: List },
                { mode: 'map', icon: MapPin },
                { mode: 'dashboard', icon: BarChart3 },
              ].map(({ mode, icon: Icon }) => (
                <Button
                  key={mode}
                  variant="ghost"
                  size="sm"
                  className={`h-8 px-3 ${viewMode === mode ? 'bg-white dark:bg-slate-700 shadow-sm' : ''}`}
                  onClick={() => changeView(mode)}
                  data-testid={`view-${mode}-btn`}
                >
                  <Icon className="w-4 h-4" />
                </Button>
              ))}
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle-btn"
              className="relative"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filter Bar */}
        {showFilters && (
          <Card className="border-slate-200 dark:border-slate-700" data-testid="filter-bar">
            <CardContent className="py-4">
              <div className="flex flex-wrap items-end gap-4">
                <FilterSelect
                  label="Manufacturer"
                  value={filterManufacturer}
                  onChange={setFilterManufacturer}
                  options={manufacturers.map(m => ({ value: m, label: m }))}
                  testId="filter-manufacturer"
                />
                <FilterSelect
                  label="Inverter Type"
                  value={filterType}
                  onChange={setFilterType}
                  options={deviceTypes.map(t => ({ value: t, label: t }))}
                  testId="filter-type"
                />
                <FilterSelect
                  label="Battery"
                  value={filterBattery}
                  onChange={setFilterBattery}
                  options={[
                    { value: 'yes', label: 'Has Battery' },
                    { value: 'no', label: 'No Battery' },
                  ]}
                  testId="filter-battery"
                />
                <FilterSelect
                  label="Status"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { value: 'online', label: 'Online' },
                    { value: 'offline', label: 'Offline' },
                    { value: 'fault', label: 'Fault' },
                  ]}
                  testId="filter-status"
                />
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters-btn">
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                )}
              </div>
              {activeFilterCount > 0 && (
                <p className="text-xs text-slate-500 mt-3" data-testid="filter-count-text">
                  Showing {filteredDevices.length} of {devices.length} devices
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Platform Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {platforms.map((p) => (
            <Card key={p.id} className={`border-slate-200 dark:border-slate-700 ${!p.configured ? 'opacity-50' : ''}`} data-testid={`platform-${p.id}`}>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${p.configured && !p.error ? 'bg-emerald-500' : p.error ? 'bg-red-500 animate-pulse' : 'bg-slate-300 dark:bg-slate-600'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{p.name}</p>
                  <p className="text-xs text-slate-500" title={p.error || ''}>
                    {p.error
                      ? <span className="text-red-500">{p.error.length > 60 ? p.error.slice(0, 60) + '...' : p.error}</span>
                      : p.configured ? 'Connected'
                      : 'Not configured — add API key in Settings'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Devices" value={overview?.total_devices || 0} icon={Server} color="text-blue-600 bg-blue-50 dark:bg-blue-900/20" testId="stat-total" />
          <StatCard label="Online" value={overview?.online_count || 0} icon={Wifi} color="text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20" testId="stat-online" />
          <StatCard label="Offline" value={overview?.offline_count || 0} icon={WifiOff} color="text-slate-500 bg-slate-50 dark:bg-slate-800" testId="stat-offline" />
          <StatCard label="Faults" value={overview?.fault_count || 0} icon={AlertTriangle} color="text-red-600 bg-red-50 dark:bg-red-900/20" testId="stat-faults" />
        </div>

        {/* Devices */}
        {filteredDevices.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700">
            <CardContent className="py-16 text-center">
              <Activity className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
                {activeFilterCount > 0 ? 'No Matching Devices' : 'No Devices Found'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                {activeFilterCount > 0
                  ? 'Try adjusting your filters to see more devices.'
                  : 'No inverters found on configured platforms. Check your API credentials.'}
              </p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" className="mt-4" onClick={clearFilters}>Clear Filters</Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'cards' ? (
          <CardsView devices={filteredDevices} onViewDevice={handleViewDevice} />
        ) : viewMode === 'table' ? (
          <TableView devices={filteredDevices} onViewDevice={handleViewDevice} />
        ) : viewMode === 'map' ? (
          <MapView devices={filteredDevices} onViewDevice={handleViewDevice} />
        ) : (
          <DashboardView devices={filteredDevices} onViewDevice={handleViewDevice} />
        )}

        {/* Device Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="device-detail-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" />
                {selectedDevice?.name || selectedDevice?.sn}
              </DialogTitle>
              <p id="device-detail-desc" className="sr-only">Device monitoring details</p>
            </DialogHeader>
            {detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                <DeviceDetailContent
                  device={selectedDevice}
                  detail={deviceDetail}
                  realtime={realtimeData}
                  generation={generationData}
                />
                <div className="border-t pt-3 flex items-center gap-3">
                  {selectedDevice?.customer_name ? (
                    <div className="text-sm text-slate-600">
                      <span className="text-slate-400">Linked to:</span>{' '}
                      <span className="font-medium">{selectedDevice.customer_name}</span>
                      {selectedDevice.postcode && <span className="text-slate-400 ml-1">({selectedDevice.postcode})</span>}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-300"
                      onClick={() => { setDetailOpen(false); setLinkDialog({ open: true, device: selectedDevice }); }}
                      data-testid="link-to-customer-btn"
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      Link to Customer
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Device-Customer Link Dialog */}
        <LinkDeviceDialog
          open={linkDialog.open}
          device={linkDialog.device}
          onClose={() => setLinkDialog({ open: false, device: null })}
          onLinked={() => { setLinkDialog({ open: false, device: null }); fetchOverview(); }}
        />
      </div>
    </Layout>
  );
};

/* ──────── Filter Select ──────── */
const FilterSelect = ({ label, value, onChange, options, testId }) => (
  <div className="flex flex-col gap-1.5 min-w-[160px]">
    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9" data-testid={testId}>
        <SelectValue placeholder={`All ${label}s`} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/* ──────── Sub-Components ──────── */
const StatCard = ({ label, value, icon: Icon, color, testId }) => (
  <Card className="border-slate-200 dark:border-slate-700" data-testid={testId}>
    <CardContent className="py-4 px-4 flex items-center gap-3">
      <div className={`p-2.5 rounded-lg ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
      </div>
    </CardContent>
  </Card>
);

const DeviceStatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG[0];
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.color} gap-1`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </Badge>
  );
};

const PlatformBadge = ({ device }) => (
  <Badge variant="outline" className="text-xs gap-1">
    <div className={`w-1.5 h-1.5 rounded-full ${PLATFORM_COLORS[device.platform] || 'bg-slate-400'}`} />
    {device.manufacturer || (device.platform === 'foxess' ? 'Fox ESS' : device.platform)}
  </Badge>
);

/* ──────── Cards View ──────── */
const CardsView = ({ devices, onViewDevice }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="cards-view">
    {devices.map((d) => (
      <Card
        key={`${d.platform}-${d.sn}`}
        className="border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => onViewDevice(d)}
        data-testid={`device-card-${d.sn}`}
      >
        <CardContent className="pt-5 pb-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 dark:text-white truncate">{d.name || d.sn}</h3>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{d.sn}</p>
            </div>
            <DeviceStatusBadge status={d.status} />
          </div>
          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div>
              <p className="text-xs text-slate-400">Power</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {typeof d.power === 'number' && d.power > 0 ? `${d.power.toFixed(2)} kW` : typeof d.pvPower === 'number' && d.pvPower > 0 ? `${d.pvPower.toFixed(2)} kW` : '--'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Today</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {typeof d.generation_today === 'number' && d.generation_today > 0 ? `${d.generation_today.toFixed(1)} kWh` : '--'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400">{typeof d.battery_soc === 'number' ? 'Battery' : 'Total'}</p>
              <p className="text-sm font-semibold text-slate-900 dark:text-white">
                {typeof d.battery_soc === 'number' && d.battery_soc > 0 ? `${d.battery_soc}%` : typeof d.generation_total === 'number' && d.generation_total > 0 ? `${d.generation_total.toFixed(0)} kWh` : '--'}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-2">
              <PlatformBadge device={d} />
              {d.has_battery && (
                <Badge variant="outline" className="text-xs gap-1">
                  <Battery className="w-3 h-3" /> Battery
                </Badge>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400" />
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

/* ──────── Table View ──────── */
const TableView = ({ devices, onViewDevice }) => (
  <Card className="border-slate-200 dark:border-slate-700" data-testid="table-view">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            {['Device', 'Serial Number', 'Platform', 'Type', 'Battery', 'Status', 'Power', 'Today', 'Total', ''].map(h => (
              <th key={h} className={`text-xs font-semibold text-slate-500 uppercase tracking-wide px-4 py-3 ${h === 'Power' || h === 'Today' || h === 'Total' || h === '' ? 'text-right' : 'text-left'}`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr
              key={`${d.platform}-${d.sn}`}
              className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
              onClick={() => onViewDevice(d)}
              data-testid={`device-row-${d.sn}`}
            >
              <td className="px-4 py-3"><p className="text-sm font-medium text-slate-900 dark:text-white">{d.name || d.sn}</p></td>
              <td className="px-4 py-3"><code className="text-xs text-slate-500 font-mono">{d.sn}</code></td>
              <td className="px-4 py-3"><PlatformBadge device={d} /></td>
              <td className="px-4 py-3"><span className="text-xs text-slate-600 dark:text-slate-400">{d.product_family || d.type || '--'}</span></td>
              <td className="px-4 py-3">
                {d.has_battery ? (
                  <Badge variant="outline" className="text-xs gap-1"><Battery className="w-3 h-3" /> Yes</Badge>
                ) : (
                  <span className="text-xs text-slate-400">No</span>
                )}
              </td>
              <td className="px-4 py-3"><DeviceStatusBadge status={d.status} /></td>
              <td className="px-4 py-3 text-right text-sm font-medium text-slate-900 dark:text-white">{typeof d.power === 'number' ? `${d.power.toFixed(2)} kW` : '--'}</td>
              <td className="px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-300">{typeof d.generation_today === 'number' ? `${d.generation_today.toFixed(1)} kWh` : '--'}</td>
              <td className="px-4 py-3 text-right text-sm text-slate-700 dark:text-slate-300">{typeof d.generation_total === 'number' ? `${d.generation_total.toFixed(0)} kWh` : '--'}</td>
              <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-slate-400" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Card>
);

/* ──────── Map View ──────── */
const MapView = ({ devices, onViewDevice }) => {
  const [geoDevices, setGeoDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const geocodeDevices = async () => {
      setLoading(true);
      const results = [];
      for (const dev of devices) {
        if (dev.latitude && dev.longitude) {
          results.push({ ...dev, lat: dev.latitude, lng: dev.longitude });
          continue;
        }
        if (dev.postcode) {
          try {
            const res = await api.get(`/monitoring/geocode/${encodeURIComponent(dev.postcode)}`);
            if (res.data?.latitude && res.data?.longitude) {
              results.push({ ...dev, lat: res.data.latitude, lng: res.data.longitude });
              continue;
            }
          } catch {}
        }
        // Skip devices without location
      }
      setGeoDevices(results);
      setLoading(false);
    };
    geocodeDevices();
  }, [devices]);

  const getColor = (status) => {
    if (status === 1) return '#22c55e'; // green - online
    if (status === 2) return '#ef4444'; // red - fault
    return '#94a3b8'; // grey - offline
  };

  // UK center fallback
  const center = geoDevices.length > 0
    ? [geoDevices.reduce((s, d) => s + d.lat, 0) / geoDevices.length, geoDevices.reduce((s, d) => s + d.lng, 0) / geoDevices.length]
    : [52.5, -1.5];

  return (
    <Card className="border-slate-200 dark:border-slate-700" data-testid="map-view">
      <CardContent className="p-0 overflow-hidden rounded-lg" style={{ height: '600px' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full bg-slate-50">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mr-3" />
            <span className="text-slate-500">Loading map locations...</span>
          </div>
        ) : geoDevices.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full bg-slate-50 text-slate-400">
            <MapPin className="w-16 h-16 mb-4" />
            <p className="text-lg font-medium">No devices with location data</p>
            <p className="text-sm mt-1">Add postcodes to customer records to see devices on the map</p>
          </div>
        ) : (
          <MapContainer center={center} zoom={7} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {geoDevices.map(dev => (
              <CircleMarker
                key={dev.sn}
                center={[dev.lat, dev.lng]}
                radius={10}
                fillColor={getColor(dev.status)}
                fillOpacity={0.9}
                color="#fff"
                weight={2}
                eventHandlers={{ click: () => onViewDevice(dev) }}
              >
                <Popup>
                  <div className="text-sm min-w-[180px]">
                    <p className="font-bold">{dev.name || dev.sn}</p>
                    {dev.customer_name && <p className="text-slate-600">{dev.customer_name}</p>}
                    {dev.address && <p className="text-slate-500 text-xs">{dev.address}</p>}
                    {dev.postcode && <p className="text-slate-500 text-xs">{dev.postcode}</p>}
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: getColor(dev.status) }} />
                      <span>{dev.status_text || 'Unknown'}</span>
                    </div>
                    {dev.power > 0 && <p className="mt-1 font-medium">{dev.power.toFixed(2)} kW</p>}
                    <p className="text-xs text-slate-400 mt-1">{dev.manufacturer} · {dev.platform}</p>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}
      </CardContent>
      {geoDevices.length > 0 && (
        <div className="p-3 border-t flex items-center gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Online ({geoDevices.filter(d => d.status === 1).length})</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-slate-400 inline-block" /> Offline ({geoDevices.filter(d => d.status === 0 || d.status === 3).length})</div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-500 inline-block" /> Fault ({geoDevices.filter(d => d.status === 2).length})</div>
          <span className="ml-auto">{geoDevices.length} of {devices.length} devices mapped</span>
        </div>
      )}
    </Card>
  );
};

/* ──────── Dashboard View ──────── */
const DashboardView = ({ devices, onViewDevice }) => {
  const online = devices.filter(d => d.status === 1);
  const offline = devices.filter(d => d.status === 0 || d.status === 3);
  const faulty = devices.filter(d => d.status === 2);
  const totalPower = devices.reduce((sum, d) => sum + (d.power || 0), 0);
  const totalToday = devices.reduce((sum, d) => sum + (d.generation_today || 0), 0);

  return (
    <div className="space-y-6" data-testid="dashboard-view">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 dark:border-slate-700 bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/30 dark:to-slate-900">
          <CardContent className="py-5 text-center">
            <Zap className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{totalPower.toFixed(2)} kW</p>
            <p className="text-xs text-slate-500 mt-1">Total Current Output</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-slate-900">
          <CardContent className="py-5 text-center">
            <Sun className="w-8 h-8 text-amber-500 mx-auto mb-2" />
            <p className="text-3xl font-bold text-slate-900 dark:text-white">{totalToday.toFixed(1)} kWh</p>
            <p className="text-xs text-slate-500 mt-1">Generated Today</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-slate-900">
          <CardContent className="py-5 text-center">
            <Activity className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-3xl font-bold text-slate-900 dark:text-white">
              {devices.length > 0 ? Math.round((online.length / devices.length) * 100) : 0}%
            </p>
            <p className="text-xs text-slate-500 mt-1">Fleet Uptime</p>
          </CardContent>
        </Card>
      </div>

      {faulty.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Faults ({faulty.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {faulty.map(d => <MiniDeviceCard key={`${d.platform}-${d.sn}`} device={d} onClick={() => onViewDevice(d)} />)}
          </div>
        </div>
      )}
      {online.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Wifi className="w-4 h-4" /> Online ({online.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {online.map(d => <MiniDeviceCard key={`${d.platform}-${d.sn}`} device={d} onClick={() => onViewDevice(d)} />)}
          </div>
        </div>
      )}
      {offline.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <WifiOff className="w-4 h-4" /> Offline ({offline.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {offline.map(d => <MiniDeviceCard key={`${d.platform}-${d.sn}`} device={d} onClick={() => onViewDevice(d)} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const MiniDeviceCard = ({ device: d, onClick }) => (
  <Card
    className="border-slate-200 dark:border-slate-700 cursor-pointer hover:shadow-sm transition-shadow"
    onClick={onClick}
    data-testid={`mini-card-${d.sn}`}
  >
    <CardContent className="py-3 px-4 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <DeviceStatusBadge status={d.status} />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{d.name || d.sn}</p>
          <p className="text-xs text-slate-500 font-mono truncate">{d.sn}</p>
        </div>
      </div>
      <div className="text-right flex-shrink-0 ml-3">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{typeof d.power === 'number' ? `${d.power.toFixed(2)} kW` : '--'}</p>
        <p className="text-xs text-slate-500">{typeof d.generation_today === 'number' ? `${d.generation_today.toFixed(1)} kWh today` : ''}</p>
      </div>
    </CardContent>
  </Card>
);

/* ──────── Device Detail Dialog Content ──────── */
const DeviceDetailContent = ({ device, detail, realtime, generation }) => {
  if (!device) return null;
  return (
    <div className="space-y-4" data-testid="device-detail-content">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">Serial Number</p>
          <p className="font-mono text-sm">{device.sn}</p>
        </div>
        <div className="flex items-center gap-2">
          <PlatformBadge device={device} />
          <DeviceStatusBadge status={device.status} />
        </div>
      </div>

      {/* Summary for non-Fox ESS devices */}
      {device.platform !== 'foxess' && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="py-4">
            <div className="grid grid-cols-3 gap-4">
              <GenStat label="Power" value={device.power} unit="kW" />
              <GenStat label="Today" value={device.generation_today} unit="kWh" />
              <GenStat label="Total" value={device.generation_total} unit="kWh" />
            </div>
          </CardContent>
        </Card>
      )}

      {generation && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Sun className="w-4 h-4 text-amber-500" /> Power Generation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <GenStat label="Today" value={generation.today} unit="kWh" />
              <GenStat label="This Month" value={generation.month} unit="kWh" />
              <GenStat label="This Year" value={generation.year} unit="kWh" />
              <GenStat label="Lifetime" value={generation.cumulative} unit="kWh" />
            </div>
          </CardContent>
        </Card>
      )}

      {realtime && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4 text-blue-500" /> Real-Time Data</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <RtStat icon={Sun} label="PV Power" value={realtime.pvPower} unit="kW" color="text-amber-500" />
              <RtStat icon={Plug} label="Load" value={realtime.loadsPower} unit="kW" color="text-blue-500" />
              <RtStat icon={ArrowDownToLine} label="Grid Import" value={realtime.gridConsumptionPower} unit="kW" color="text-red-500" />
              <RtStat icon={ArrowUpFromLine} label="Grid Export" value={realtime.feedinPower} unit="kW" color="text-emerald-500" />
              <RtStat icon={Battery} label="Battery SoC" value={realtime.SoC} unit="%" color="text-purple-500" />
              <RtStat icon={Thermometer} label="Inv. Temp" value={realtime.invTemperation} unit="C" color="text-orange-500" />
            </div>
          </CardContent>
        </Card>
      )}

      {detail && (
        <Card className="border-slate-200 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Server className="w-4 h-4 text-slate-500" /> Device Info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {detail.device_name && <InfoRow label="Name" value={detail.device_name} />}
              {detail.device_type && <InfoRow label="Type" value={detail.device_type} />}
              {detail.station_name && <InfoRow label="Station" value={detail.station_name} />}
              {detail.module_sn && <InfoRow label="Module SN" value={detail.module_sn} />}
              {detail.has_battery && <InfoRow label="Battery" value={detail.battery_type || 'Yes'} />}
              {detail.battery_soc > 0 && <InfoRow label="Battery SoC" value={`${detail.battery_soc}%`} />}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const GenStat = ({ label, value, unit }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-lg font-bold text-slate-900 dark:text-white">
      {typeof value === 'number' ? value.toFixed(1) : '--'}
      <span className="text-xs font-normal text-slate-400 ml-1">{unit}</span>
    </p>
  </div>
);

const RtStat = ({ icon: Icon, label, value, unit, color }) => (
  <div className="flex items-start gap-2">
    <Icon className={`w-4 h-4 mt-0.5 ${color}`} />
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-sm font-semibold text-slate-900 dark:text-white">
        {typeof value === 'number' ? value.toFixed(2) : '--'} {unit}
      </p>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{value}</p>
  </div>
);

export default Monitoring;

/* ──────── Link Device to Customer Dialog ──────── */
function LinkDeviceDialog({ open, device, onClose, onLinked }) {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      setSearch('');
      customersAPI.getAll({}).then(r => setCustomers(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    }
  }, [open]);

  const filtered = search
    ? customers.filter(c =>
        `${c.first_name} ${c.last_name} ${c.address} ${c.postcode} ${c.email}`.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 15)
    : customers.slice(0, 15);

  const handleLink = async (customer) => {
    if (!device?.sn) return;
    setLinking(true);
    try {
      await deviceLinksAPI.link(device.sn, customer.id);
      toast.success(`Linked ${device.name || device.sn} to ${customer.first_name} ${customer.last_name}`);
      onLinked();
    } catch {
      toast.error('Failed to link device');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Link Device to Customer
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm p-3 bg-slate-50 rounded-lg mb-3">
          <p><span className="text-slate-500">Device:</span> <span className="font-medium">{device?.name || device?.sn}</span></p>
          <p><span className="text-slate-500">SN:</span> <span className="font-mono text-xs">{device?.sn}</span></p>
          <p><span className="text-slate-500">Platform:</span> {device?.manufacturer} ({device?.platform})</p>
        </div>
        <input
          type="text"
          placeholder="Search customers by name, address, postcode..."
          className="w-full p-2 border rounded-lg text-sm mb-2"
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="link-customer-search"
        />
        <div className="flex-1 overflow-y-auto border rounded-lg divide-y max-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm">No customers found</div>
          ) : (
            filtered.map(c => (
              <div
                key={c.id}
                className="p-3 cursor-pointer hover:bg-blue-50 flex justify-between items-center"
                onClick={() => !linking && handleLink(c)}
                data-testid={`link-customer-${c.id}`}
              >
                <div>
                  <p className="font-medium text-sm">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-slate-500">{c.address}{c.postcode ? `, ${c.postcode}` : ''}</p>
                </div>
                {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4 text-slate-300" />}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
